import { RELEASE_HEADER } from "@mfa/v5-contracts";
import { useSyncExternalStore } from "react";
import { type UnsavedWork, unsavedWork } from "./unsaved-work.js";

/**
 * Which release the page is talking to (x-v5-release, the commit the answering server was built
 * from). A tab keeps its JavaScript across deploys, so after a release that changed the wire in
 * place (#376's error body) it misreads answers until someone reloads it. Every response the API
 * client reads passes its release here:
 *
 * - the page's own release is the one its build was stamped with (__APP_BUILD_ID__: the image
 *   build passes SOURCE_COMMIT to `npm run build` as APP_BUILD_ID). A build that is not stamped
 *   ("dev", local development) takes the first release it reads instead;
 * - a response naming another release means a newer one is live: the shell offers a reload,
 *   once per release (dismissing it hides it until yet another release answers);
 * - a response this build cannot decode, from another release, reloads the page once, guarded
 *   per release in sessionStorage so a broken server never loops. The same failure from the
 *   page's own release changes nothing. While the page holds unsaved work (unsaved-work.ts),
 *   that reload waits: the shell offers it instead, and it runs once the last work is saved or
 *   discarded, unless the release that answers by then is the page's own again or the person
 *   reloaded first.
 *
 * A response a cache may have kept (a Cache-Control that lets it be reused without asking the
 * server, as the catalog snapshot's does) names the release that stored it, not the one running
 * now, so its release is not read.
 */

export interface ReleaseState {
  /**
   * The release the page was loaded from: its build's stamp, or for a build without one the
   * first release it read. Null until it reads one.
   */
  loaded: string | null;
  /** Another release a response named, while its offer is not dismissed; null otherwise. */
  newer: string | null;
}

export interface ReleaseWatchOptions {
  /** What a reload does; `window.location.reload()` by default, which keeps the URL. */
  reload?: () => void;
  /** Where the once-per-release reload guard lives; sessionStorage by default. */
  storage?: () => Storage | null;
  /** The release the build was stamped with (a commit sha), or "dev"; __APP_BUILD_ID__ by default. */
  buildId?: string;
  /** The work a reload would lose; the page's registry (unsaved-work.ts) by default. */
  unsaved?: UnsavedWork;
}

/** The sessionStorage key that marks a release the page already reloaded for. */
export const RELOAD_GUARD_PREFIX = "mfa.v5.releaseReload.";

const defaultStorage = (): Storage | null => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
};

/** A stamped build id: a full commit sha, as vite.config.ts accepts one. */
const STAMPED = /^[0-9a-f]{40}$/;

const REUSABLE = /(?:^|,)\s*(?:max-age\s*=\s*0*[1-9]|s-maxage|stale-while-revalidate|immutable)/i;

/**
 * The release a response names, or null: none sent (a server that knows no release, a proxy's
 * page), or a response a cache may have answered for the server.
 */
export function releaseOf(headers: { get(name: string): string | null }): string | null {
  const release = headers.get(RELEASE_HEADER)?.trim();
  if (!release) return null;
  if (REUSABLE.test(headers.get("cache-control") ?? "")) return null;
  return release;
}

export class ReleaseWatch {
  private state: ReleaseState;
  private readonly dismissed = new Set<string>();
  private readonly listeners = new Set<() => void>();
  private readonly reloadPage: () => void;
  private readonly storage: () => Storage | null;
  private readonly unsaved: UnsavedWork;
  /** The last release a response named. */
  private latest: string | null = null;
  /** The release an automatic reload waits on while work is unsaved; null when none waits. */
  private pending: string | null = null;

  constructor(options: ReleaseWatchOptions = {}) {
    this.reloadPage = options.reload ?? (() => window.location.reload());
    this.storage = options.storage ?? defaultStorage;
    this.unsaved = options.unsaved ?? unsavedWork;
    const buildId = options.buildId ?? __APP_BUILD_ID__;
    this.state = { loaded: STAMPED.test(buildId) ? buildId : null, newer: null };
    this.unsaved.subscribe(() => {
      // A microtask, so a surface that lets go and another that takes hold in the same commit
      // (an applied YAML edit becoming an unsaved spec) never reads as no work held.
      if (this.pending !== null) queueMicrotask(() => this.settle());
    });
  }

  /** A response's release (null when it named none); an unstamped build takes the first as its own. */
  observe(release: string | null): void {
    if (!release) return;
    this.latest = release;
    if (this.state.loaded === null) {
      this.set({ loaded: release, newer: null });
      return;
    }
    if (release === this.state.loaded || release === this.state.newer) return;
    if (this.dismissed.has(release)) return;
    this.set({ ...this.state, newer: release });
  }

  /**
   * A response this build could not decode, with the release it named: from another release
   * than the page's, the page reloads once for that release, or, while work is unsaved, offers
   * the reload and runs it once the work is saved. True when it reloaded.
   */
  undecodable(release: string | null): boolean {
    if (!release || this.state.loaded === null || release === this.state.loaded) return false;
    // Without somewhere to mark the reload, a reload could loop: offer it instead.
    if (!this.mayReload(release)) return false;
    if (this.unsaved.has()) {
      this.pending = release;
      this.dismissed.delete(release);
      if (this.state.newer === null) this.set({ ...this.state, newer: release });
      return false;
    }
    return this.reloadOnce(release);
  }

  /** The offer's Reload button: reloads now, unsaved work or not, and nothing waits after it. */
  reload(): void {
    this.pending = null;
    this.reloadPage();
  }

  /** Hides the offer for the release it names; another release offers again. */
  dismiss(): void {
    if (this.state.newer === null) return;
    this.dismissed.add(this.state.newer);
    this.set({ ...this.state, newer: null });
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ReleaseState => this.state;

  /** The waiting reload, once no work is held: only while another release than the page's answers. */
  private settle(): void {
    const release = this.pending;
    if (release === null || this.unsaved.has()) return;
    this.pending = null;
    if (this.latest === this.state.loaded) return;
    this.reloadOnce(release);
  }

  private mayReload(release: string): boolean {
    try {
      const storage = this.storage();
      return storage !== null && storage.getItem(`${RELOAD_GUARD_PREFIX}${release}`) === null;
    } catch {
      return false;
    }
  }

  private reloadOnce(release: string): boolean {
    if (!this.mayReload(release)) return false;
    try {
      this.storage()?.setItem(`${RELOAD_GUARD_PREFIX}${release}`, "1");
    } catch {
      return false;
    }
    this.reloadPage();
    return true;
  }

  private set(next: ReleaseState): void {
    this.state = next;
    for (const listener of this.listeners) listener();
  }
}

/** The page's one watch: the API client reports to it and the shell's banner reads it. */
export const releaseWatch = new ReleaseWatch();

/** The newer release on offer (null when none), for a component. */
export function useNewerRelease(watch: ReleaseWatch = releaseWatch): string | null {
  return useSyncExternalStore(watch.subscribe, watch.getSnapshot, watch.getSnapshot).newer;
}
